import { useCallback, useEffect, useMemo } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { LoadingSkeleton } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";

import SubscriptionSummary from "../SubscriptionSummary";
import { useSubscriptionCards } from "../../contexts/SubscriptionCardsContext";

import styles from "./SubscriptionManagement.module.scss";
import { InstitutionSettingsContextProvider } from "../../contexts/InstitutionSettingsContext";
import CardSelector from "../CardSelector";
import MerchantDetailsDrawer from "../MerchantDetailsDrawer";
import PageLayout from "../../../src/components/PageLayout";

const SubscriptionManagement = () => {
  const { l10n } = useLocalization();
  const { cardUuid } = useParams<{ cardUuid?: string }>();
  const { cards, isLoading } = useSubscriptionCards();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();

  const headerTitle = l10n.getString(
    "subscription-management-title",
    null,
    "Recurring card transactions"
  );

  const cardId = useMemo<API.CardId | undefined>(() => {
    if (!cardUuid && cards.length > 0) {
      return cards[0].id;
    }
    return cardUuid as API.CardId | undefined;
  }, [cardUuid, cards]);

  const handleCardChange = useCallback(
    (newCardId: API.CardId) => {
      navigate(`/cards/${newCardId}/subscriptions`);
    },
    [navigate]
  );

  useEffect(() => {
    if (cards.length === 0 && !isLoading) {
      sendNotification({
        type: "negative",
        text: "Unable to find any cards.",
      });
      navigate("/dashboard", { replace: true });
    }
  }, [cards, isLoading, navigate, sendNotification]);

  useEffect(() => {
    if (cardId && !cards.find((card) => card.id === cardId) && !isLoading) {
      sendNotification({
        type: "negative",
        text: l10n.getString(
          "subscription-card-not-found",
          null,
          "Unable to find that card."
        ),
      });
      navigate("/dashboard", { replace: true });
    }
  }, [cards, isLoading, cardId, navigate, sendNotification, l10n]);

  let content;

  if (isLoading) {
    content = <LoadingSkeleton isLoading />;
  } else if (cards.length === 0) {
    content = (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>{headerTitle}</h1>
        </div>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p>
            {l10n.getString(
              "subscription-no-cards",
              null,
              "You don't have any cards to manage subscriptions for."
            )}
          </p>
        </div>
      </div>
    );
  } else {
    content = (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>{headerTitle}</h1>
          <CardSelector
            cardId={cardId}
            cards={cards}
            onChange={handleCardChange}
          />
        </div>
        {cardId && <SubscriptionSummary cardId={cardId} />}
        <Routes>
          <Route
            path=":transactionUuid"
            element={<MerchantDetailsDrawer cardId={cardId!} />}
          />
        </Routes>
      </div>
    );
  }

  return (
    <InstitutionSettingsContextProvider>
      <PageLayout>{content}</PageLayout>
    </InstitutionSettingsContextProvider>
  );
};

export default SubscriptionManagement;
